import { Box3, Vector2Tuple } from 'three';

import { Schema, SchemaFieldFurniture, SchemaValues } from '../schema/index.js';
import { FInstanceNode } from '../types/index.js';

import {
  FArrangementGrid,
  FArrangementGridSchema,
  FArrangementGridSchemaFields,
  FArrangementGridValues,
} from './FArrangementGrid.js';

const bbox = new Box3();
const TAU = Math.PI * 2;

export interface FArrangementCocktailSchemaFields extends FArrangementGridSchemaFields {
  tableType: SchemaFieldFurniture;
}

export interface FArrangementCocktailSchema extends FArrangementGridSchema {
  fields: FArrangementCocktailSchemaFields;
}

export interface FArrangementCocktailValues {}

export type FArrangementCocktailAttributes = Partial<
  SchemaValues<FArrangementCocktailSchemaFields>
> &
  FArrangementGridValues &
  FArrangementCocktailValues;

export class FArrangementCocktail<
  TSchema extends FArrangementCocktailSchema = FArrangementCocktailSchema,
  TValues extends FArrangementCocktailValues = FArrangementCocktailValues,
  TFCompositionSchema extends Schema = Schema,
  TFCompositionValues extends Record<string, any> = {}
> extends FArrangementGrid<
  FArrangementCocktailSchema,
  FArrangementCocktailValues,
  TFCompositionSchema,
  TFCompositionValues
> {
  constructor(
    schema: TSchema,
    initialValues?: FArrangementCocktailAttributes & TValues & TFCompositionValues
  ) {
    super(schema, {
      maxElements: Infinity,
      elementSize: [0, 0],
      ...(initialValues as any),
    });
    this.setValue('getMaxElements', this.getMaxElements);
  }

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    let { elements } = this.getValues();
    return this.getSafeIterations(
      Math.min(ignoreMaxElements ? Infinity : elements, maxColumns * maxRows)
    );
  }

  getNode(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ) {
    const { elementRotation, orientation } =
      this.getValues() as SchemaValues<FArrangementCocktailSchemaFields>;

    const theta = orientation * TAU;

    const { position } = params;

    const [id] = this.getNodeTypes();
    return {
      id: id,
      uuid: this.getNodeUuid(id, idx),
      position: position,
      rotation: -theta + elementRotation * TAU,
    };
  }

  getMaxElements = () => {
    return this.computeNodes(true).length;
  };

  protected getCompositionNodes(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceNode[] {
    const { elementRotation, orientation } = this.getValues();

    const theta = orientation * TAU;

    const { position } = params;

    const [id] = this.getNodeTypes();
    return [
      {
        type: 'table',
        id: id,
        position: position,
        rotation: -theta + elementRotation * TAU,
      },
    ];
  }

  getNodeTypes(): number[] {
    const { tableType } = this.getValues() as SchemaValues<FArrangementCocktailSchemaFields> &
      FArrangementCocktailValues;

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [tableType];
  }

  getInstanceBoundingBox(): Box3 {
    const [chair] = this.getObjects();

    return bbox.setFromObject(chair);
  }
}
